import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import AudioPlayer from "./components/AudioPlayer";
import RouteSEO from "./components/RouteSEO";
import LocalizedDocument from "./components/LocalizedDocument";
import Home from "./pages/Home";
import Gear from "./pages/Gear";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import PaymentSuccess from "./pages/PaymentSuccess";
import ManualPayment from "./pages/ManualPayment";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import NotFound from "./pages/NotFound";
import CustomerLogin from "./pages/CustomerLogin";
import CustomerRegister from "./pages/CustomerRegister";
import CustomerVerifyOtp from "./pages/CustomerVerifyOtp";
import CustomerDashboard from "./pages/CustomerDashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Unsubscribe from "./pages/Unsubscribe";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import EngineerMarketplacePreview from "./pages/EngineerMarketplacePreview";
import EngineerAdmin from "./pages/EngineerAdmin";
import CustomerServiceOrders from "./pages/CustomerServiceOrders";
import ServiceOrderAdmin from "./pages/ServiceOrderAdmin";
import VstMakerPreview from "./pages/VstMakerPreview";
import VstAssets from "./pages/VstAssets";
import VstMakerAccess from "./pages/VstMakerAccess";
import VstMakerBilling from "./pages/VstMakerBilling";
import VstPartnerDashboard from "./pages/VstPartnerDashboard";
import EngineerAccess from "./pages/EngineerAccess";
import EngineerAcceptInvite from "./pages/EngineerAcceptInvite";
import EngineerVerifyOtp from "./pages/EngineerVerifyOtp";
import EngineerForgotPassword from "./pages/EngineerForgotPassword";
import EngineerResetPassword from "./pages/EngineerResetPassword";
import EngineerDashboard from "./pages/EngineerDashboard";
import EngineerShop from "./pages/EngineerShop";
import EngineerProfileEditor from "./pages/EngineerProfileEditor";
import EngineerSubmissionView from "./pages/EngineerSubmissionView";
import AdminOnlyPreview from "./components/AdminOnlyPreview";
import { AudioProvider } from "./context/AudioContext";
import { AuthProvider } from "./context/AuthContext";
import { SERVER_STATUS_EVENT } from "./lib/api";

function App() {
  const marketplaceEnabled = process.env.REACT_APP_ENGINEER_MARKETPLACE_ENABLED === "true";
  const [serverNotice, setServerNotice] = useState(null);

  useEffect(() => {
    const handleStatus = (event) => {
      const status = event.detail?.status;
      if (status === "online") setServerNotice(null);
      else setServerNotice(event.detail || { status: "maintenance" });
    };
    const handleOffline = () => setServerNotice({
      status: "offline",
      message: "Koneksi internet terputus. Data Anda tetap aman dan dapat dilanjutkan setelah tersambung kembali.",
    });
    const handleOnline = () => setServerNotice(null);
    window.addEventListener(SERVER_STATUS_EVENT, handleStatus);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    if (navigator.onLine === false) handleOffline();
    return () => {
      window.removeEventListener(SERVER_STATUS_EVENT, handleStatus);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  return (
    <div className="App">
      {serverNotice && (
        <div className="fixed inset-x-3 top-3 z-[200] mx-auto flex max-w-3xl items-center justify-between gap-4 rounded-2xl border border-amber-300/25 bg-[#17130a]/95 px-4 py-3 text-left shadow-2xl shadow-black/40 backdrop-blur" role="status" aria-live="polite">
          <div>
            <div className="text-xs font-bold text-amber-100">{serverNotice.status === "offline" ? "Koneksi terputus" : "Server sedang diperbarui"}</div>
            <div className="mt-0.5 text-[11px] leading-5 text-amber-100/70">{serverNotice.message || "Kami sedang memulai ulang layanan. Data Anda tetap aman dan halaman dapat dicoba kembali sebentar lagi."}</div>
          </div>
          <button type="button" onClick={() => window.location.reload()} className="shrink-0 rounded-lg border border-amber-200/20 px-3 py-2 text-[10px] font-bold text-amber-100 hover:bg-amber-100/10">Coba lagi</button>
        </div>
      )}
      <AuthProvider>
        <AudioProvider>
          <BrowserRouter>
            <RouteSEO />
            <LocalizedDocument />
            <Navbar />
            <main className="min-h-screen">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/songs" element={<Navigate to="/" replace />} />
                <Route path="/gear" element={<Gear />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/:id" element={<ProductDetail />} />
                <Route path="/vst-maker" element={<VstAssets />} />
                <Route path="/vst-studio" element={<VstMakerAccess />} />
                <Route path="/vst-assets" element={<Navigate to="/vst-maker" replace />} />
                <Route path="/vst-maker/billing" element={<VstMakerBilling />} />
                <Route path="/vst-maker/partner" element={<VstPartnerDashboard />} />
                {marketplaceEnabled && <Route path="/engineers" element={<EngineerMarketplacePreview />} />}
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/manual/:transactionId" element={<ManualPayment />} />
                <Route path="/login" element={<CustomerLogin />} />
                <Route path="/register" element={<CustomerRegister />} />
                <Route path="/register/verify" element={<CustomerVerifyOtp />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/dashboard" element={<CustomerDashboard />} />
                {marketplaceEnabled && <Route path="/dashboard/services" element={<CustomerServiceOrders />} />}
                {marketplaceEnabled && <Route path="/engineer/login" element={<EngineerAccess />} />}
                {marketplaceEnabled && <Route path="/engineer/register" element={<EngineerAccess />} />}
                {marketplaceEnabled && <Route path="/engineer/register/verify" element={<EngineerVerifyOtp />} />}
                {marketplaceEnabled && <Route path="/engineer/forgot-password" element={<EngineerForgotPassword />} />}
                {marketplaceEnabled && <Route path="/engineer/reset-password" element={<EngineerResetPassword />} />}
                {marketplaceEnabled && <Route path="/engineer/accept-invite" element={<EngineerAcceptInvite />} />}
                {marketplaceEnabled && <Route path="/engineer/dashboard" element={<EngineerDashboard />} />}
                {marketplaceEnabled && <Route path="/engineer/shop" element={<EngineerShop />} />}
                {marketplaceEnabled && <Route path="/engineer/profile" element={<EngineerProfileEditor />} />}
                {marketplaceEnabled && <Route path="/engineer/submission" element={<EngineerSubmissionView />} />}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route
                  path="/admin/preview/engineers"
                  element={
                    <AdminOnlyPreview>
                      <EngineerMarketplacePreview preview />
                    </AdminOnlyPreview>
                  }
                />
                <Route
                  path="/admin/engineers"
                  element={
                    <AdminOnlyPreview>
                      <EngineerAdmin />
                    </AdminOnlyPreview>
                  }
                />
                <Route
                  path="/admin/service-orders"
                  element={
                    <AdminOnlyPreview>
                      <ServiceOrderAdmin />
                    </AdminOnlyPreview>
                  }
                />
                <Route
                  path="/admin/vst-maker"
                  element={
                    <AdminOnlyPreview>
                      <VstMakerPreview />
                    </AdminOnlyPreview>
                  }
                />
                <Route
                  path="/admin/vst-maker/billing-preview"
                  element={<Navigate to="/vst-maker/billing" replace />}
                />
                <Route
                  path="/admin/vst-assets"
                  element={
                    <AdminOnlyPreview>
                      <VstAssets preview />
                    </AdminOnlyPreview>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
            <AudioPlayer />
            <Toaster theme="dark" position="top-right" />
          </BrowserRouter>
        </AudioProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
